import { memo, useCallback, useMemo, useSyncExternalStore } from "react";

import { useCart } from "@shopify/hydrogen-react";
import {
  type CartLine,
  type MoneyV2,
} from "@shopify/hydrogen-react/storefront-api-types";

import { useCartActiveProduct } from "@cart/CartActiveProductContext";
import { useCartGiftWithPurchase } from "@cart/CartGiftWithPurchaseContext";
import { useAutoRemoveOnwardProduct } from "@react/hooks/useAutoRemoveOnwardProduct";
import { useSectionSettings } from "@sections/Root/SectionSettingsContext";
import { TablescapeContext } from "@tablescape/context";
import {
  type CartSectionSettings,
  type TablescapeState,
} from "@tablescape/state";
import {
  getIntelligemsComparePriceByVariantId,
  getShopifyGlobalId,
  hasIntelligemsLineItemAttribute,
} from "@tablescape/utilities";

import { CartBaseView } from "./CartBase.view";

const CartBaseBase = () => {
  const {
    cart,
    ui: { cartDrawerOpen, cartLineAdding, cartLineRemoving, cartLineUpdating },
  } = useSyncExternalStore<TablescapeState>(
    TablescapeContext.onAll,
    TablescapeContext.getSnapshot
  );
  const cartLineChanging = useMemo(
    () => cartLineAdding || cartLineUpdating || cartLineRemoving,
    [cartLineAdding, cartLineUpdating, cartLineRemoving]
  );

  useAutoRemoveOnwardProduct(cartLineUpdating || cartLineRemoving);

  const { toggleCartDrawer } = useCartActiveProduct();

  const { cost, lines, status, totalQuantity } = useCart();

  const {
    content: { baseDrawer: baseDrawerContent },
    meta: {
      localization: { country },
    },
    settings: {
      baseDrawer: baseDrawerSettings,
      cartSpendingGiftWithPurchase: cartSpendingGiftWithPurchaseSettings,
    },
  } = useSectionSettings<CartSectionSettings>();

  const { relatedCartLines } = useCartGiftWithPurchase();

  const discountedTotalAmount = useMemo<Partial<MoneyV2> | undefined>(() => {
    if (
      cartLineChanging ||
      !Array.isArray(lines) ||
      !cost?.totalAmount?.currencyCode
    ) {
      return undefined;
    }

    let discountedTotal = 0;

    lines.forEach((line) => {
      if (line?.cost?.totalAmount?.amount && line?.quantity) {
        if (hasIntelligemsLineItemAttribute(line)) {
          const intelligemsPrice = getIntelligemsComparePriceByVariantId(
            line?.merchandise?.id
          );

          if (intelligemsPrice) {
            discountedTotal +=
              intelligemsPrice * line.quantity -
              parseInt(line.cost.totalAmount.amount);
          }
        } else if (line?.cost?.compareAtAmountPerQuantity?.amount) {
          discountedTotal +=
            parseInt(line.cost.compareAtAmountPerQuantity.amount) *
              line.quantity -
            parseInt(line.cost.totalAmount.amount);
        } else if (
          line?.merchandise?.price?.amount &&
          parseInt(line.merchandise.price.amount) * line.quantity >
            parseInt(line.cost.totalAmount.amount)
        ) {
          discountedTotal +=
            parseInt(line.merchandise.price.amount) * line.quantity -
            parseInt(line.cost.totalAmount.amount);
        }
      }
    });

    return {
      currencyCode: cost.totalAmount.currencyCode,
      amount: discountedTotal.toString(),
    };
  }, [cartLineChanging, cost?.totalAmount?.currencyCode, lines]);

  const cartItemPlaceholderCount = useMemo(() => {
    const addingLines = cart?.lines;
    if (addingLines) {
      const cartLineAddingVariantIds = addingLines
        .filter((line): line is CartLine => !!(line && line?.id))
        .map((line) => getShopifyGlobalId("ProductVariant", line.id));
      let count = cartLineAddingVariantIds.length;
      if (!lines || lines?.length === 0) {
        return count;
      }
      lines.forEach((line) => {
        const lineVariantId = line?.merchandise?.id;
        if (lineVariantId && cartLineAddingVariantIds.includes(lineVariantId)) {
          count -= 1;
        }
      });

      return count;
    }

    return 0;
  }, [cart, lines]);

  const showDiscount = useMemo(
    () => !!(discountedTotalAmount && discountedTotalAmount?.amount !== "0"),
    [discountedTotalAmount]
  );

  const upsellsEnabled = useMemo(
    () => country !== "AU" || window.Shopify?.shop?.startsWith("our-place-anz"),
    [country]
  );

  const handleCloseCartDrawer = useCallback(() => {
    toggleCartDrawer({
      cartDrawerOpen: false,
      cartDrawerUserToggled: false,
    });
  }, [toggleCartDrawer]);

  return (
    <CartBaseView
      cartItemPlaceholderCount={cartItemPlaceholderCount}
      cartLineAdding={cartLineAdding}
      cartLineChanging={cartLineChanging}
      cartLineRemoving={cartLineRemoving}
      cartLineUpdating={cartLineUpdating}
      cartSpendingGiftWithPurchaseSettings={
        cartSpendingGiftWithPurchaseSettings
      }
      content={baseDrawerContent}
      discountedTotalAmount={discountedTotalAmount}
      handleCloseCartDrawer={handleCloseCartDrawer}
      isDrawerVisible={cartDrawerOpen}
      relatedCartLines={relatedCartLines}
      settings={baseDrawerSettings}
      showDiscount={showDiscount}
      status={status}
      totalQuantity={totalQuantity ?? 0}
      upsellsEnabled={upsellsEnabled}
    />
  );
};

export const CartBase = memo(CartBaseBase);
